import { useQuery } from '@apollo/client';
import {
  GetSemesterPaymentPlansDocument,
} from '@sandtech-hub-monorepo/ehub-client';
import { Application, Enrollment } from '../types';
import {
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  getIconFromAbbreviation,
  PaymentBannerMessages,
  PlannedCoursesView,
  ProgramEnrollmentPaymentBanner,
  ProgramEnrollmentStatusLabel,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { AssistanceSection } from './AssistanceSection';
import { ApplicationRequirements } from './ApplicationRequirements';
import { ActiveConductCasesBanners } from './ActiveConductCasesBanners';
import { MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppRoutesPaths } from '../../../routes/types';

const PAYMENT_BANNER_MESSAGES: PaymentBannerMessages = {
  success: (
    <>
      <b>Payment up to date:</b> Your fees are up to date, ensuring
      uninterrupted access to your program.
    </>
  ),
  fallback: {
    red: (
      <>
        <b>Payment required.</b> Please make a payment.
      </>
    ),
    amber: (
      <>
        <b>Payment due soon.</b> Please make a payment.
      </>
    ),
    blue: (
      <>
        <b>Payment coming up.</b> Please make a payment.
      </>
    ),
  },
  beforeFirst: {
    blue: ({ formattedDate }) => (
      <>
        <b>Payment coming up:</b> Pay your starting fee by{' '}
        <b>{formattedDate}</b> to complete your enrollment and begin the
        semester.
      </>
    ),
    amber: ({ formattedDate }) => (
      <>
        <b>Payment due soon:</b> Pay your starting fee by <b>{formattedDate}</b>{' '}
        to complete your enrollment and begin the semester.
      </>
    ),
    red: ({ formattedDate }) => (
      <>
        <b>Payment overdue:</b> Pay your starting fee by <b>{formattedDate}</b>{' '}
        to avoid being withdrawn from your program.
      </>
    ),
  },
  firstPaymentMade: {
    blue: ({ termBalance }) => (
      <>
        <b>First payment made:</b> Pay <b>${termBalance.toFixed(2)}</b> by the
        end of the semester to ensure uninterrupted access to your program.
      </>
    ),
  },
  transitionNoOutstanding: {
    blue: ({ formattedDate }) => (
      <>
        <b>Payment coming up:</b> Pay your starting fee by{' '}
        <b>{formattedDate}</b> to advance to the next semester.
      </>
    ),
    amber: ({ formattedDate }) => (
      <>
        <b>Payment due soon:</b> Pay your starting fee by <b>{formattedDate}</b>{' '}
        to advance to the next semester.
      </>
    ),
    red: ({ formattedDate }) => (
      <>
        <b>Payment overdue:</b> Pay your starting fee by <b>{formattedDate}</b>{' '}
        to avoid being withdrawn from your program.
      </>
    ),
  },
  transitionWithOutstanding: {
    blue: ({ formattedDate, outstandingBalance }) => (
      <>
        <b>Payment coming up:</b> Pay your starting fee and clear outstanding
        balance <b>${outstandingBalance.toFixed(2)}</b> by{' '}
        <b>{formattedDate}</b> to advance to the next semester.
      </>
    ),
    amber: ({ formattedDate, outstandingBalance }) => (
      <>
        <b>Payment due soon:</b> Pay your starting fee and clear outstanding
        balance <b>${outstandingBalance.toFixed(2)}</b> by{' '}
        <b>{formattedDate}</b> to advance to the next semester.
      </>
    ),
    red: ({ formattedDate, outstandingBalance }) => (
      <>
        <b>Payment overdue:</b> Pay your starting fee and clear outstanding
        balance <b>${outstandingBalance.toFixed(2)}</b> by{' '}
        <b>{formattedDate}</b> to avoid being withdrawn from your program.
      </>
    ),
  },
  finalOutstanding: {
    red: ({ outstandingBalance }) => (
      <>
        <b>Payment required:</b> Please clear your outstanding balance of{' '}
        <b>${outstandingBalance.toFixed(2)}</b> to avoid being withdrawn from
        your program.
      </>
    ),
  },
};

interface Props {
  enrollment: Enrollment;
  application?: Application;
}

export const ApprovedApplicationView = ({ enrollment, application }: Props) => {
  const navigate = useNavigate();

  const { data: paymentPlansData } = useQuery(GetSemesterPaymentPlansDocument, {
    fetchPolicy: 'cache-first',
  });

  const paymentPlan = paymentPlansData?.viewer?.paymentPlans?.[0];

  const conductCases = enrollment.academicConductCases ?? [];

  return (
    <>
      <Card className="px-6 py-9 border-heading">
        {/* Payment Banner */}
        {paymentPlan && (
          <div className="mb-6">
            <ProgramEnrollmentPaymentBanner
              paymentPlan={paymentPlan}
              paymentFAQsLink="/payments/faqs"
              messages={PAYMENT_BANNER_MESSAGES}
              showPaymentPlanName={false}
              showFAQLink={false}
            />
          </div>
        )}

        {/* Program Header Card */}
        <div className="border border-heading rounded-lg p-6 mb-6 flex flex-col gap-6">
          <div className="flex items-start gap-5">
            {/* Program Icon */}
            <div className="shrink-0">
              {getIconFromAbbreviation(
                enrollment.program.abbreviation,
                'w-[70px] h-[70px]'
              )}
            </div>

            {/* Program Info */}
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="flex gap-2">
                <h2 className="text-base font-semibold text-primary">
                  {enrollment.program.name}
                </h2>
                <ProgramEnrollmentStatusLabel
                  status={enrollment.status}
                />
                <div className="flex-1" />
                {/* Actions Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger className="shrink-0 p-1 rounded-md hover:bg-muted transition-colors cursor-pointer">
                    <MoreHorizontal className="h-5 w-5 text-primary" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => navigate(AppRoutesPaths.Transcript)}
                    >
                      Preview Transcript
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <p className="text-sm text-foreground/80">
                Your application for the {enrollment.program.name} at Patten
                University has been approved!
                <br />
                You can now proceed to The School of Entrepreneurship &
                Technology at Patten University.
              </p>
              {/* Academic Standing Banners */}
              <ActiveConductCasesBanners
                conductCases={conductCases}
                cumulativeGPA={enrollment?.cumulativeGPA}
              />
            </div>
          </div>
        </div>

        {/* Courses Section */}
        <div>
          <PlannedCoursesView
            plannedCourses={enrollment.plannedCourses}
          />
        </div>
      </Card>

      {/* Application Requirements */}
      {application && (
        <Card className="px-6 py-3 border-heading">
          <ApplicationRequirements
            enrollment={enrollment}
            application={application}
          />
        </Card>
      )}

      {/* Assistance Section */}
      <AssistanceSection />
    </>
  );
};
